<template>
    <b-modal
        :visible="visible"
        :title="title"
        size="xl"
        centered
        ok-only
        ok-title="닫기"
        content-class="stl-settlement-portal"
        @change="$emit('input', $event)"
        @hidden="$emit('hidden')"
    >
        <div class="settlement-page">
            <div class="stl-row" style="margin-bottom: 12px; justify-content: space-between;">
                <div>
                    <span v-if="matrix && matrix.status" :class="statusClass(matrix.status)">{{ statusLabel(matrix.status) }}</span>
                    <span class="stl-hint" style="margin-left: 8px;">행 클릭 → 세대 상세</span>
                </div>
                <div class="stl-row" style="gap: 6px;">
                    <button type="button" class="stl-btn stl-btn-sm" :disabled="exporting || !matrix?.rows?.length" @click="exportMatrix">
                        Excel
                    </button>
                    <button type="button" class="stl-btn stl-btn-sm" :disabled="!hasPrev" @click="$emit('prev')">
                        <i class="fa fa-chevron-left"></i> 이전 월
                    </button>
                    <button type="button" class="stl-btn stl-btn-sm" :disabled="!hasNext" @click="$emit('next')">
                        다음 월 <i class="fa fa-chevron-right"></i>
                    </button>
                </div>
            </div>

            <div v-if="loading" class="stl-loading"><b-spinner small variant="secondary" /><span>{{ L.monthMatrixLoading }}</span></div>
            <div v-else-if="!matrix || !matrix.rows.length" class="stl-empty"><p>해당 월 정산 데이터가 없습니다.</p></div>
            <div v-else class="stl-table-container">
                <table class="stl-table">
                    <thead>
                        <tr>
                            <th>호</th>
                            <th class="num">면적(㎡)</th>
                            <th class="num">사용량({{ unitLabel(usageUnit) }})</th>
                            <th class="num">{{ L.usageShare }}</th>
                            <th v-if="showAreaCol" class="num">{{ L.areaShare }}</th>
                            <th class="num">조정</th>
                            <th class="num">합계(원)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in matrix.rows"
                            :key="row.sysNodeIdx"
                            class="clickable-row"
                            @click="$emit('select-row', row)"
                        >
                            <td class="ho">{{ row.roomName }}</td>
                            <td class="num">{{ fmtArea(row.exclusiveArea) }}</td>
                            <td class="num">{{ fmtUsage(row.usageQty) }}</td>
                            <td class="num">{{ fmtWon(row.individualCost) }}</td>
                            <td v-if="showAreaCol" class="num">{{ fmtWon(row.allocatedCommonCost) }}</td>
                            <td class="num">{{ fmtWon(row.manualAdjustAmount) }}</td>
                            <td class="num"><strong>{{ fmtWon(row.totalAmount) }}</strong></td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>합계</td>
                            <td></td>
                            <td class="num">{{ fmtUsage(matrix.footer.usageQty) }}</td>
                            <td class="num">{{ fmtWon(matrix.footer.individualCost) }}</td>
                            <td v-if="showAreaCol" class="num">{{ fmtWon(matrix.footer.allocatedCommonCost) }}</td>
                            <td class="num">{{ fmtWon(matrix.footer.manualAdjustAmount) }}</td>
                            <td class="num">{{ fmtWon(matrix.footer.totalAmount) }}</td>
                        </tr>
                        <tr>
                            <td :colspan="showAreaCol ? 6 : 5" class="text-right" style="font-weight: 600;">{{ L.totalAmount }} / {{ L.areaSharePool }} / {{ L.remainder }}</td>
                            <td class="num">
                                {{ fmtWon(matrix.footer.kepcoTotalAmount) }} /
                                {{ fmtWon(matrix.footer.commonPoolAmount) }} /
                                {{ fmtWon(floorRemainder) }}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { fmtWon, fmtUsage, fmtArea, fmtYm, unitLabel, statusLabel, statusClass, SETTLEMENT_LABELS } from "../utils/settlementFormat.js";
import { buildMatrixCsvRows, downloadCsvBlob, floorRemainder as calcFloorRemainder } from "../utils/ledgerExport.js";

export default {
    name: "LedgerMonthMatrixModal",
    props: {
        value: { type: Boolean, default: false },
        regYm: { type: String, default: "" },
        matrix: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        energySource: { type: String, default: "Elec" },
        hasPrev: { type: Boolean, default: false },
        hasNext: { type: Boolean, default: false },
    },
    data() {
        return { exporting: false };
    },
    computed: {
        visible: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit("input", v);
            },
        },
        L() {
            return SETTLEMENT_LABELS;
        },
        title() {
            const base = `${SETTLEMENT_LABELS.monthMatrix} — ${fmtYm(this.regYm)}`;
            if (this.energySource && this.energySource !== "ALL") {
                return `${base} · ${this.energyLabel(this.energySource)}`;
            }
            return base;
        },
        usageUnit() {
            return this.matrix?.rows?.[0]?.usageUnit || (this.energySource === "Gas" ? "Nm3" : this.energySource === "Water" ? "m3" : "kWh");
        },
        showAreaCol() {
            return this.energySource === "Elec";
        },
        floorRemainder() {
            if (!this.matrix?.footer) return null;
            return calcFloorRemainder({
                kepcoTotalAmount: this.matrix.footer.kepcoTotalAmount,
                totalAmountSum: this.matrix.footer.totalAmount,
            });
        },
    },
    methods: {
        fmtWon,
        fmtUsage,
        fmtArea,
        fmtYm,
        unitLabel,
        statusLabel,
        statusClass,
        exportMatrix() {
            if (!this.matrix?.rows?.length) return;
            this.exporting = true;
            try {
                const lines = buildMatrixCsvRows(this.regYm, this.matrix, { showAreaCol: this.showAreaCol });
                const suffix = this.energySource && this.energySource !== "ALL" ? `_${this.energySource}` : "";
                downloadCsvBlob(`settlement_matrix_${this.regYm}${suffix}.csv`, lines);
            } finally {
                this.exporting = false;
            }
        },
        energyLabel(e) {
            if (e === "Gas") return "가스";
            if (e === "Water") return "수도";
            return "전기";
        },
    },
};
</script>
